/**
 * Card-grid filter strip rendered inside the unified `DataViewFilterPanel`.
 *
 * Tiles only — the active parent's sub-cards used to render here as a
 * nested teal box; that has moved to a flat chip row in the panel's
 * "Reason" section (which derives the active parent itself, no callback
 * plumbing required).
 *
 * The outer Paper chrome lives on `DataViewFilterPanel`.
 */
import React from 'react'
import { Box } from '@mui/material'
import IssueCard from './IssueCard'
import type { IssueCardEntry } from '../../../config/exceptions/types'

interface IssueCardStripProps {
  cards: IssueCardEntry[]
  activeKey: string | null
  onSelect: (key: string | null) => void
  /**
   * Custom active-state matcher. Default: `card.key === activeKey`. Sales-
   * compare overrides this so a parent card lights up when `activeKey`
   * is one of its sub-mismatches (e.g. clicking 'amounts.tax' lights up
   * the 'amounts' parent).
   */
  isCardActive?: (cardKey: string | null, activeKey: string | null) => boolean
}

const defaultIsActive = (cardKey: string | null, activeKey: string | null): boolean =>
  cardKey === activeKey

export const findActiveSubCards = (
  cards: IssueCardEntry[],
  activeKey: string | null,
  isCardActive: (cardKey: string | null, activeKey: string | null) => boolean = defaultIsActive,
): IssueCardEntry | null => {
  const parent = cards.find(
    (c) => c.subCards && c.subCards.cards.length > 0 && isCardActive(c.key, activeKey),
  )
  return parent ?? null
}

const IssueCardStrip: React.FC<IssueCardStripProps> = ({
  cards,
  activeKey,
  onSelect,
  isCardActive = defaultIsActive,
}) => {
  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'stretch',
        gap: 1,
      }}
    >
      {cards.map((card) => (
        <IssueCard
          key={card.key ?? '__anchor__'}
          label={card.label}
          count={card.count}
          active={isCardActive(card.key, activeKey)}
          icon={card.icon}
          onClick={() => onSelect(card.key)}
        />
      ))}
    </Box>
  )
}

export default IssueCardStrip
